import { func } from "prop-types";
import React, { useEffect } from "react";

import styled from "styled-components/macro";

import { useMediaSettings } from "Signups/stateHooks";

import FnrButton from "UI/FnrButton3";
import FnrIcon from "UI/FnrIcon";
import FnrHorizontalGap from "UI/commonContainers/FnrHorizontalGap"

import IconButton from "./IconButton";
import ProgressBar from "./ProgressBar";
import { RemoveMediaButton } from "./RemoveMediaButton";
import { useUploadFile } from "./useUploadFile";

ImageHandler.propTypes = {
  setError: func.isRequired,
};

/*
Note: This component handles initial media type picker buttons, image uploading, and image display.
Reasons for this:  
  1. needs to propery handle if user click's "cancel" from the browser file picker UI
      - i.e. not set mediaType to "image" until upload actually starts
  2. needs to allow upload from both the initial image button click as well as "Replace" button click
*/

export default function ImageHandler({ setError }) {
  const { mediaSettings, setMediaSettings, setMediaType } = useMediaSettings();

  const imageUrl = mediaSettings?.imageUrl;
  const applyShadow = !String(imageUrl).endsWith(".png"); // skip shadow for PNGs in case using transparency

  const clearErrors = () => {
    setError(undefined);
    setFileSizeErrorMessage(undefined);
  }


  let fileInputRef = React.createRef();
  const { handleFileUpload, isUploading, percentUploaded, error, setFileSizeErrorMessage } =
    useUploadFile({
      fileInputRef,
      onUploadStart: () => {
        clearErrors();
        setMediaType("image");
      },
      onUploadComplete: ({ photoID }) => {
        setMediaSettings({
          ...mediaSettings,
          type: "image",
          photoID,
        });
      },
      onError: () => {
        setMediaType("none");
      },
    });

  const handleUploadImageButtonClick = () => {
    clearErrors();
    fileInputRef.current.click();
  };

  const handleReplaceImageClick = () => {
    clearErrors();
    fileInputRef.current.click();
  };

  useEffect(() => {
    setError(error);
  }, [error, setError]);

  return (
    <SFnrHorizontalGap gap={24} fullWidth>
      <ImageContent>
        <FileInput
          data-cy="Signup_ImageInput"
          aria-label="file-input"
          ref={fileInputRef}
          type="file"
          onChange={handleFileUpload}
          accept=".jpg,.jpeg,.png,.gif,.webp"
        />

        {isUploading ? (
          <ProgressBarWrapper>
            <ProgressBar percent={percentUploaded} />
          </ProgressBarWrapper>
        ) : null}

        {imageUrl && !isUploading ? (
          <ImageDisplay>
            <ButtonWrapper gap={8} fullWidth>
              <FnrButton
                data-cy="Signup_ReplaceImageButton"
                color="blue"
                size="small"
                onClick={handleReplaceImageClick}
              >
                <FnrIcon far fa-image />
                &nbsp;Replace
              </FnrButton>
              <RemoveMediaButton />
            </ButtonWrapper>
            <Img data-cy="Signup_Image" src={imageUrl} $applyShadow={applyShadow} />
          </ImageDisplay>
        ) : null}

        {!mediaSettings?.type || mediaSettings.type === "none" ? (
          <MediaTypeButtons>
            <IconButton
              data-cy="Signup_AddImageButton"
              label="Add an Image"
              aria-label="upload-image"
              icon="fa-image"
              onClick={handleUploadImageButtonClick}
            />
            <IconButton
              data-cy="Signup_AddVideoButton"
              label="Add a Video"
              aria-label="set-media-video"
              icon="fa-video"
              onClick={() => {
                setMediaType("video");
                setError(undefined);
              }}
            />
          </MediaTypeButtons>
        ) : null}
      </ImageContent>
    </SFnrHorizontalGap>
  );
}

const SFnrHorizontalGap = styled(FnrHorizontalGap)`
  width: 100%;
`;

const ImageContent = styled.div`
  position: relative;
  border-radius: 8px;
  width: 100%;
  height: 100%;
  display: grid;
  place-items: center;
`;

const FileInput = styled.input`
  display: none;
`;

const ProgressBarWrapper = styled.div`
  width: 200px;
`;

const ImageDisplay = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  height: 100%;
  border-radius: 8px;
  cursor: default;
`;

const Img = styled.img`
  max-width: 100%;
  max-height: 400px;
  border-radius: 8px;
  box-shadow: ${({ $applyShadow }) =>
    $applyShadow ? "rgba(0, 0, 0, 0.4) 1px 1px 8px" : null};
`;

const ButtonWrapper = styled(FnrHorizontalGap)`
  position: absolute;
  opacity: 0;
  transition: opacity 0.3s;
  top: 0;
  transform: translateY(-50%);
  min-width: 100%;
  ${ImageDisplay}:focus, ${ImageDisplay}:hover & {
    opacity: 1;
  }
`;

const MediaTypeButtons = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 16px;
`;